import EmojiPicker, { EmojiStyle, Theme } from 'emoji-picker-react'
import React, { useState } from 'react'

import { useAppearance } from '@fourthwall/appearance'
import { Loading, PillSwitch, Popper, Upload } from '@fourthwall/components'
import { useCreateImageResources } from '@modules/Dashboard/Memberships/hooks/useCreateImageResources'

import * as S from './styled'

type Props = {
  emoji: string
  onChange: (
    emoji:
      | { type: 'Emoji'; emojiIcon: string }
      | { type: 'ImageResource'; url: string; imageResourceId: number },
  ) => void
}

export const EmojiInput = ({ emoji, onChange }: Props) => {
  const [isOpen, setOpen] = useState(false)
  const [tab, setTab] = useState<'emoji' | 'image'>('emoji')
  const { appearance } = useAppearance()
  const { createImageResources, isLoading } = useCreateImageResources()

  const getEmoji = () => {
    if (emoji.startsWith('http')) {
      return <S.Image src={emoji} alt="emoji" />
    }

    return <S.Emoji>{emoji}</S.Emoji>
  }

  const toggleOpen = () => {
    setOpen((prevIsOpen) => {
      return !prevIsOpen
    })
  }

  const getTheme = () => {
    if (appearance === 'light dark') {
      return Theme.AUTO
    }

    if (appearance === 'dark') {
      return Theme.DARK
    }

    return Theme.LIGHT
  }

  const handleFileDrop = (files: File[]) => {
    if (files[0]) {
      createImageResources({
        files: [files[0]],
        onSuccess: (images) => {
          onChange({
            type: 'ImageResource',
            url: images[0].resizedUrl,
            imageResourceId: images[0].id,
          })
          setOpen(false)
        },
      })
    }
  }

  return (
    <Popper
      isOpen={isOpen}
      referenceComponent={
        <S.Container onClick={toggleOpen}>
          {getEmoji()}
          Icon
        </S.Container>
      }
      popperProps={{
        placement: 'bottom-start',
      }}
      style={{ paddingTop: 8 }}
      onClose={() => setOpen(false)}
    >
      <S.PopperContainer>
        <Loading isLoading={isLoading}>
          <S.PillSwitchContainer>
            <PillSwitch
              items={[
                { label: 'Emoji', value: 'emoji' },
                { label: 'Image', value: 'image' },
              ]}
              value={tab}
              onChange={(value) => (isLoading ? undefined : setTab(value as 'emoji' | 'image'))}
            />
          </S.PillSwitchContainer>
          {tab === 'emoji' && (
            <EmojiPicker
              autoFocusSearch={false}
              previewConfig={{ showPreview: false }}
              skinTonesDisabled
              theme={getTheme()}
              emojiStyle={EmojiStyle.NATIVE}
              onEmojiClick={(emoji) => {
                onChange({
                  type: 'Emoji',
                  emojiIcon: emoji.emoji,
                })
                setOpen(false)
              }}
              width="100%"
            />
          )}
          {tab === 'image' && (
            <>
              <S.UploadContainer>
                <Upload
                  accept={{
                    'image/*': [],
                  }}
                  maxSize={4194304}
                  disabled={isLoading}
                  iconSize={32}
                  orientation="horizontal"
                  title=""
                  onFileDrop={handleFileDrop}
                />
              </S.UploadContainer>
              <S.UploadInfo>Recommended min. 32 x 32 px icon size</S.UploadInfo>
            </>
          )}
        </Loading>
      </S.PopperContainer>
    </Popper>
  )
}
